/**
 * ReferenceTablesSection — the "Reference tables" subsection of the MetaInfo
 * manager's enum list (and of the same list in Settings → Meta Info).
 *
 * A reference-table flag has no per-column row anywhere: it is declared once at
 * the *target* table and silently turns every column referencing it into an
 * implicit lookup enum. This list is therefore the only place the user can see
 * which tables carry the flag — and the only place a flag can be dropped
 * without walking to a referencing column's details dialog.
 *
 * One row per flag: the qualified table (bare table name on schemaless
 * engines), the shared label column its names come from when one is declared,
 * and a remove button running the shared confirm flow.
 */

import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type { ReferenceTableDeclaration, TableDisplayColumn } from '@/types/electron';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import { useInvalidateMetaInfo } from '../../hooks/useMetaInfo';
import { findTableDisplayColumn } from '../../utils/tableDisplayColumn';
import { qualifyLookupTarget } from '../../utils/lookupEnumTarget';
import { confirmRemoveReferenceTable } from './referenceTableActions';
import { ProvenanceBadge } from './ProvenanceBadge';

export type ReferenceTablesSectionProps = {
  referenceTables: ReferenceTableDeclaration[];
  /** Shared per-table label columns — where a flagged table's names come from. */
  tableDisplayColumns: TableDisplayColumn[];
  /** Removal needs both; without them the button is inert. */
  connectionId?: string;
  database?: string;
  engine?: DatabaseEngineId;
};

export function ReferenceTablesSection({
  referenceTables,
  tableDisplayColumns,
  connectionId,
  database,
  engine,
}: ReferenceTablesSectionProps) {
  const { track } = useAnalytics();
  const invalidate = useInvalidateMetaInfo();
  if (referenceTables.length === 0) return null;
  return (
    <div className="mt-4">
      <h4 className="text-xs font-medium text-muted-foreground mb-1">
        Reference tables
      </h4>
      <ul className="space-y-1">
        {referenceTables.map((r) => {
          const qualified = qualifyLookupTarget(r.schema, r.table);
          const isOnDemand = r.mode === 'on-demand';
          // `findTableDisplayColumn`, not `resolveTableDisplayColumn`: the
          // manager has no live column list to validate a stale entry against,
          // and the name the user chose is exactly what to echo back here.
          const entry = findTableDisplayColumn({ tableDisplayColumns }, r.schema, r.table);
          const labelColumn = entry && entry.column !== '' ? entry.column : null;
          // Inert-flag check: `labelColumn` is `null` both when no
          // `tableDisplayColumns` entry was ever persisted for this table AND
          // when the persisted entry is the explicit-none sentinel
          // (`column === ''`) — see `findTableDisplayColumn`'s doc comment.
          // That is the honest signal available here: the manager has no live
          // schema for this table, so — unlike `resolveLookupLabelColumn`
          // (grids/FK popover) — it can NOT run the name-based heuristic
          // (`pickDisplayColumn`) to guess a column. A row could still get
          // labels once the heuristic runs elsewhere, which is exactly what
          // the note now says: the grids may well pick one, this is the place
          // to be explicit about it.
          const isInert = !labelColumn;
          return (
            <li
              key={qualified}
              className="flex flex-col gap-0.5"
              data-testid={`metainfo-reference-table-row-${qualified}`}
            >
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate min-w-0">
                  <span className="font-mono" translate="no">
                    {qualified}
                  </span>
                  {isOnDemand && (
                    <span
                      className="text-[10px] text-muted-foreground ml-1"
                      data-testid={`metainfo-reference-table-mode-${qualified}`}
                    >
                      · names on demand
                    </span>
                  )}
                  {labelColumn && (
                    // The label column is a `tableDisplayColumns` entry, which
                    // has no row of its own anywhere — so its own provenance
                    // badge rides here, right beside the value it flags.
                    <span
                      className="text-[10px] text-muted-foreground ml-1"
                      data-testid={`metainfo-reference-table-label-${qualified}`}
                    >
                      · label:{' '}
                      <span className="font-mono" translate="no">
                        {labelColumn}
                      </span>{' '}
                      <ProvenanceBadge provenance={entry?.provenance} />
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-1 shrink-0">
                <ProvenanceBadge provenance={r.provenance} />
                <Button
                  size="icon"
                  variant="ghost"
                  disabled={!connectionId || !database}
                  onClick={() =>
                    connectionId &&
                    database &&
                    confirmRemoveReferenceTable({
                      connectionId,
                      database,
                      schema: r.schema,
                      table: r.table,
                      engine,
                      mode: r.mode ?? 'cached',
                      trigger: 'metainfo-manager',
                      track,
                      invalidate,
                    })
                  }
                  aria-label="Stop treating this table as a reference table"
                  title="Stop treating this table as a reference table"
                  className="h-6 w-6 shrink-0 text-destructive"
                  data-testid={`metainfo-reference-table-remove-${qualified}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
                </div>
              </div>
              {isInert && (
                <span
                  className="text-[10px] text-muted-foreground/80"
                  data-testid={`metainfo-reference-table-inert-${qualified}`}
                >
                  No label column set — the grids may still pick one by name
                  (name/title/label); set one in the enum details to be
                  explicit.
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
